// src/redux/slices/drawers/drawerStackSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type DrawerType =
    | "lotAndContainerDetails"
    | "medicationConfigDrawer"
    | "containerLogDrawer"
    | "containerEntryDrawer";

export interface DrawerState {
    id: string;
    type: DrawerType;
    state: any;
    zIndex: number;
    parentId?: string;
}

export interface IDrawerStackState {
    activeDrawers: DrawerState[];
    nextZIndex: number;
}

export const initialStateDrawerStack: IDrawerStackState = {
    activeDrawers: [],
    nextZIndex: 1040,
};

const MAX_DRAWERS = 5;

const drawerStackSlice = createSlice({
    name: "drawerStack",
    initialState: initialStateDrawerStack,
    reducers: {
        addToDrawerStack: (
            state,
            action: PayloadAction<{
                id: string;
                type: DrawerType;
                state: any;
                parentId?: string;
            }>,
        ) => {
            const { id, type, state: drawerState, parentId } = action.payload;

            // Remove oldest drawer if we're at the limit
            if (state.activeDrawers.length >= MAX_DRAWERS) {
                state.activeDrawers.shift();
            }

            // Simplified structure - no unnecessary nesting
            state.activeDrawers.push({
                id,
                type,
                state: drawerState, // Direct drawer state
                zIndex: state.nextZIndex,
                parentId,
            });
            state.nextZIndex += 1;
        },
        updateDrawerState: (
            state,
            action: PayloadAction<{
                id: string;
                state: any;
            }>,
        ) => {
            const drawerIndex = state.activeDrawers.findIndex(
                (drawer) => drawer.id === action.payload.id,
            );
            if (drawerIndex !== -1) {
                state.activeDrawers[drawerIndex].state = action.payload.state;
            }
        },
        removeFromDrawerStack: (state, action: PayloadAction<string>) => {
            const drawerIndex = state.activeDrawers.findIndex(
                (drawer) => drawer.id === action.payload,
            );
            if (drawerIndex !== -1) {
                state.activeDrawers.splice(drawerIndex, 1);
            }
        },
        bringToFront: (state, action: PayloadAction<string>) => {
            const drawerIndex = state.activeDrawers.findIndex(
                (drawer) => drawer.id === action.payload,
            );
            if (drawerIndex !== -1) {
                const drawer = state.activeDrawers[drawerIndex];
                state.activeDrawers.splice(drawerIndex, 1);
                state.activeDrawers.push({
                    ...drawer,
                    zIndex: state.nextZIndex,
                });
                state.nextZIndex += 1;
            }
        },
    },
});

export const { addToDrawerStack, removeFromDrawerStack, bringToFront } = drawerStackSlice.actions;
export default drawerStackSlice.reducer;
